import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../apiConfig';

export default function RtiManager({ onSubmitting }) {
    const [officers, setOfficers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState('');

    // Form State
    const [form, setForm] = useState({
        office: '',
        officer: '',
        title: 'තොරතුරු දැනගැනීමේ නිලධාරී',
        position: 'සහකාර කෘෂිකර්ම අධ්‍යක්ෂ'
    });

    // Fetch initial RTI officers data
    const fetchOfficers = async () => {
        setLoading(true);
        try {
            const url = getApiUrl(`api_rti.php?t=${Date.now()}`);
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data) && data.length > 0) {
                    setOfficers(data);
                    try { localStorage.setItem('site_rti_officers', JSON.stringify(data)); } catch (e) {}
                    return;
                }
            }
            throw new Error('API fetch failed');
        } catch (err) {
            console.warn('RTI API failed, falling back to local files...', err);
            // Try static rti_data.json
            try {
                const fallbackRes = await fetch(`/rti_data.json?t=${Date.now()}`);
                if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    if (Array.isArray(fallbackData) && fallbackData.length > 0) {
                        setOfficers(fallbackData);
                        return;
                    }
                }
            } catch (fbErr) {
                console.warn('Static json fallback failed:', fbErr);
            }

            // LocalStorage fallback
            const local = localStorage.getItem('site_rti_officers');
            if (local) {
                try {
                    setOfficers(JSON.parse(local));
                } catch (parseErr) {
                    setOfficers([]);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOfficers();
    }, []);

    // Save or Update Officer
    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!form.office.trim() || !form.officer.trim()) {
            alert('කරුණාකර කාර්යාලය සහ නිලධාරියාගේ නම ඇතුලත් කරන්න.');
            return;
        }

        if (onSubmitting) onSubmitting(true);
        setMessage('');

        try {
            const id = editingId || `rti-${Date.now()}`;
            const newItem = {
                id,
                office: form.office.trim(),
                officer: form.officer.trim(),
                title: form.title.trim() || 'තොරතුරු දැනගැනීමේ නිලධාරී',
                position: form.position.trim()
            };

            const updatedList = editingId
                ? officers.map(item => item.id === editingId ? newItem : item)
                : [...officers, newItem];

            setOfficers(updatedList);

            // LocalStorage backup
            try {
                localStorage.setItem('site_rti_officers', JSON.stringify(updatedList));
            } catch (lsErr) {
                console.warn('LocalStorage save failed:', lsErr);
            }

            // Server sync via PHP API
            try {
                const response = await fetch(getApiUrl('api_rti.php'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedList)
                });

                if (response.ok) {
                    setMessage(editingId ? '✅ නිලධාරි විස්තර සාර්ථකව යාවත්කාලීන විය!' : '✅ නව නිලධාරියෙකු සාර්ථකව ඇතුලත් විය!');
                } else {
                    throw new Error('API server sync failed');
                }
            } catch (apiErr) {
                console.error('Server sync failed:', apiErr);
                setMessage('⚠ විස්තර Local Storage හි සුරැකිණි. සර්වර් වෙත sync වීම ප්‍රමාද විය හැක.');
            }

            // Reset form
            handleCancelEdit();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => setMessage(''), 4000);

        } catch (err) {
            console.error('Submit error:', err);
            setMessage('⚠ Error: ' + err.message);
        } finally {
            if (onSubmitting) onSubmitting(false);
        }
    };

    // Load officer to edit
    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            office: item.office || '',
            officer: item.officer || '',
            title: item.title || 'තොරතුරු දැනගැනීමේ නිලධාරී',
            position: item.position || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditingId(null);
        setForm({
            office: '',
            officer: '',
            title: 'තොරතුරු දැනගැනීමේ නිලධාරී',
            position: 'සහකාර කෘෂිකර්ම අධ්‍යක්ෂ'
        });
    };

    // Delete officer
    const handleDelete = async (id) => {
        const itemToDelete = officers.find(i => i.id === id);
        const confirmMsg = `ඔබට මෙම නිලධාරියා ලැයිස්තුවෙන් ඉවත් කිරීමට අවශ්‍ය ද?\n"${itemToDelete?.office} - ${itemToDelete?.officer}"`;
        if (!window.confirm(confirmMsg)) return;

        if (onSubmitting) onSubmitting(true);
        const updatedList = officers.filter(item => item.id !== id);
        setOfficers(updatedList);

        try {
            localStorage.setItem('site_rti_officers', JSON.stringify(updatedList));
        } catch (e) {}

        try {
            await fetch(getApiUrl('api_rti.php'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedList)
            });
            setMessage('🗑️ නිලධාරියා සාර්ථකව ඉවත් කරන ලදී.');
            setTimeout(() => setMessage(''), 3500);
        } catch (err) {
            console.error('Delete sync failed:', err);
        } finally {
            if (onSubmitting) onSubmitting(false);
        }
    };

    // Move Officer Order Up or Down
    const handleMove = async (index, direction) => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= officers.length) return;

        const updatedList = [...officers];
        const [movedItem] = updatedList.splice(index, 1);
        updatedList.splice(targetIndex, 0, movedItem);

        setOfficers(updatedList);

        try {
            localStorage.setItem('site_rti_officers', JSON.stringify(updatedList));
            await fetch(getApiUrl('api_rti.php'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedList)
            });
        } catch (err) {
            console.error('Reorder sync error:', err);
        }
    };

    // Filter officers by search term
    const filteredOfficers = officers.filter(item => {
        const matchOffice = item.office?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchOfficer = item.officer?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchPosition = item.position?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchOffice || matchOfficer || matchPosition;
    });

    return (
        <div className="rti-manager-container" style={{ padding: '10px 0' }}>
            {/* Header info banner */}
            <div style={{
                background: 'linear-gradient(135deg, #0e633d 0%, #168052 100%)',
                color: 'white',
                padding: '24px 28px',
                borderRadius: '14px',
                marginBottom: '24px',
                boxShadow: '0 4px 18px rgba(14, 99, 61, 0.15)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div>
                    <h2 style={{ margin: '0 0 6px 0', fontSize: '24px', fontWeight: '700', letterSpacing: '-0.3px' }}>
                        ⚖️ තොරතුරු දැනගැනීමේ නිලධාරීන් කළමනාකරණය (RTI Officers)
                    </h2>
                    <p style={{ margin: 0, opacity: 0.92, fontSize: '14px' }}>
                        තොරතුරු දැනගැනීමේ පනත (RTI) යටතේ පත් කර ඇති සියලුම කාර්යාල හා නිලධාරීන්ගේ විස්තර මෙතැනින් සංස්කරණය සහ අලුතින් ඇතුලත් කරන්න.
                    </p>
                </div>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.18)',
                    backdropFilter: 'blur(8px)',
                    padding: '10px 18px',
                    borderRadius: '10px',
                    textAlign: 'center'
                }}>
                    <span style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>මුළු නිලධාරීන්</span>
                    <strong style={{ fontSize: '22px' }}>{officers.length}</strong>
                </div>
            </div>

            {/* Notification alert */}
            {message && (
                <div style={{
                    padding: '14px 20px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                    fontWeight: '600',
                    fontSize: '15px',
                    backgroundColor: message.includes('⚠') ? '#fff3cd' : '#d1e7dd',
                    color: message.includes('⚠') ? '#856404' : '#0f5132',
                    border: message.includes('⚠') ? '1px solid #ffeeba' : '1px solid #badbcc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <span>{message}</span>
                    <button 
                        onClick={() => setMessage('')} 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Main Form Section */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '28px',
                marginBottom: '30px',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                border: editingId ? '2px solid #0e633d' : '1px solid #e2e8f0'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px', borderBottom: '1px solid #eef2f6', paddingBottom: '14px' }}>
                    <h3 style={{ margin: 0, fontSize: '19px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{editingId ? '✏️ නිලධාරි විස්තර යාවත්කාලීන කරන්න' : '➕ නව තොරතුරු දැනගැනීමේ නිලධාරියෙකු එක් කරන්න'}</span>
                    </h3>
                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                padding: '6px 14px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                            }}
                        >
                            ✕ අවලංගු කරන්න (Cancel)
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '22px' }}>
                        {/* Office */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                කාර්යාලය (Office) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={form.office}
                                onChange={(e) => setForm({ ...form, office: e.target.value })}
                                placeholder="උදා: සහකාර කෘෂිකර්ම අධ්‍යක්ෂ කාර්යාලය , රත්නපුර"
                                required
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '14.5px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0e633d'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>

                        {/* Officer Name */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                නිලධාරියාගේ නම (Officer Name) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={form.officer}
                                onChange={(e) => setForm({ ...form, officer: e.target.value })}
                                placeholder="උදා: පී.අයි.පී.එම් පුස්සැල්ල"
                                required
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '14.5px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0e633d'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>

                        {/* Title / Role */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                කාර්යභාරය / ලේබලය (Title)
                            </label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="තොරතුරු දැනගැනීමේ නිලධාරී"
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '14.5px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Position / Designation */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                නිල තනතුර (Designation / Position)
                            </label>
                            <input
                                type="text"
                                value={form.position}
                                onChange={(e) => setForm({ ...form, position: e.target.value })}
                                placeholder="උදා: සහකාර කෘෂිකර්ම අධ්‍යක්ෂ"
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '14.5px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button
                            type="submit"
                            style={{
                                background: '#0e633d',
                                color: 'white',
                                border: 'none',
                                padding: '12px 28px',
                                borderRadius: '8px',
                                fontSize: '15px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 3px 12px rgba(14, 99, 61, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            <span>{editingId ? '💾 යාවත්කාලීන කරන්න (Update)' : '➕ නිලධාරියා එක් කරන්න (Add Officer)'}</span>
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    padding: '12px 20px',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                අවලංගු කරන්න
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* List / Table Section */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '24px',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '18px',
                    flexWrap: 'wrap',
                    gap: '14px'
                }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>
                        📋 ඇතුලත් කර ඇති නිලධාරීන් ලැයිස්තුව ({filteredOfficers.length})
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="🔍 සොයන්න (කාර්යාලය හෝ නිලධාරියා)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                minWidth: '260px',
                                outline: 'none'
                            }}
                        />
                        <button
                            type="button"
                            onClick={fetchOfficers}
                            title="Refresh list"
                            style={{
                                padding: '8px 12px',
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            🔄
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        දත්ත ලබාගනිමින් පවතී (Loading RTI officers)...
                    </div>
                ) : filteredOfficers.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f8fafc',
                        borderRadius: '10px',
                        color: '#64748b'
                    }}>
                        {searchTerm ? 'සෙවුමට අදාල නිලධාරීන් කිසිවක් හමු නොවීය.' : 'තවමත් නිලධාරීන් ඇතුලත් කර නොමැත.'}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ background: '#0e633d', color: 'white' }}>
                                    <th style={{ padding: '14px 16px', width: '50px', textAlign: 'center' }}>#</th>
                                    <th style={{ padding: '14px 16px', width: '38%' }}>කාර්යාලය</th>
                                    <th style={{ padding: '14px 16px', width: '42%' }}>තොරතුරු දැනගැනීමේ නිලධාරි විස්තර</th>
                                    <th style={{ padding: '14px 16px', textAlign: 'right', width: '160px' }}>ක්‍රියාමාර්ග</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOfficers.map((item, index) => (
                                    <tr
                                        key={item.id || index}
                                        style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            backgroundColor: editingId === item.id ? '#f0fdf4' : (index % 2 === 0 ? '#ffffff' : '#fcfdfd'),
                                            transition: 'background-color 0.15s'
                                        }}
                                    >
                                        {/* Order number */}
                                        <td style={{ padding: '14px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                                            {index + 1}
                                        </td>

                                        {/* Office */}
                                        <td style={{ padding: '14px 16px', fontWeight: '700', color: '#0e633d', fontSize: '14.5px' }}>
                                            {item.office}
                                        </td>

                                        {/* Officer Details */}
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '15px', marginBottom: '4px' }}>
                                                {item.officer}
                                            </div>
                                            <div style={{ display: 'inline-block', fontSize: '12px', color: '#0e633d', fontWeight: '600', marginBottom: '2px' }}>
                                                {item.title || 'තොරතුරු දැනගැනීමේ නිලධාරී'}
                                            </div>
                                            {item.position && (
                                                <div style={{ fontSize: '12.5px', color: '#64748b' }}>
                                                    {item.position}
                                                </div>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td style={{ padding: '14px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {/* Reorder Buttons */}
                                            <button
                                                type="button"
                                                onClick={() => handleMove(index, 'up')}
                                                disabled={index === 0}
                                                style={{
                                                    background: '#f1f5f9',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '4px',
                                                    padding: '4px 7px',
                                                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                                                    opacity: index === 0 ? 0.4 : 1,
                                                    marginRight: '4px',
                                                    fontSize: '12px'
                                                }}
                                                title="ඉහළට ගෙන යන්න"
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMove(index, 'down')}
                                                disabled={index === officers.length - 1}
                                                style={{
                                                    background: '#f1f5f9',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '4px',
                                                    padding: '4px 7px',
                                                    cursor: index === officers.length - 1 ? 'not-allowed' : 'pointer',
                                                    opacity: index === officers.length - 1 ? 0.4 : 1,
                                                    marginRight: '8px',
                                                    fontSize: '12px'
                                                }}
                                                title="පහළට ගෙන යන්න"
                                            >
                                                ▼
                                            </button>

                                            {/* Edit */}
                                            <button
                                                type="button"
                                                onClick={() => handleEdit(item)}
                                                style={{
                                                    background: '#e0f2fe',
                                                    color: '#0284c7',
                                                    border: 'none',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    marginRight: '6px'
                                                }}
                                                title="සංස්කරණය කරන්න"
                                            >
                                                ✏️ Edit
                                            </button>

                                            {/* Delete */}
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(item.id)}
                                                style={{
                                                    background: '#fee2e2',
                                                    color: '#ef4444',
                                                    border: 'none',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                                title="ඉවත් කරන්න"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
